import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AssessmentOutlined,
  AutoAwesome,
  Close,
  Download,
  ForumOutlined,
  Schedule,
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import axios from 'axios';
import { useNavigate } from '../router';
import { useAuth } from '../contexts/AuthContext';
import radioSuiteLogo from '../assets/radiosuite-logo.png';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const ASK_PROMPTS = [
  'Add 20 tracks that match this station\'s sound and length',
  'What should I drop from Main, and what should move to Recurrent?',
  'Which artists are rotating too often this week?',
  'Search online for this week\'s new releases in our format',
];

const REPORT_STEPS = [
  'Reading the library, categories, and clocks',
  'Working out the format from the categories',
  'Checking this week\'s rotation and plays',
  'Looking up the format\'s charts',
  'Writing the adds and moves',
];

const MOVE_LABELS = { promote: 'Promote', demote: 'Demote', move: 'Move', drop: 'Drop' };

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

function stationMarket(station) {
  return [station?.location, station?.country].filter(Boolean).join(', ');
}

function shortfallNote(profile, found) {
  const asked = Number(profile?.requested) || 0;
  if (!asked || found >= asked || profile?.error) return '';
  if (profile?.batchError) return `Found ${found} of the ${asked} asked for. A later lookup failed, so the list stops there.`;
  const owned = Number(profile?.inLibrary) || 0;
  const ownedText = owned
    ? ` ${owned} more ${owned === 1 ? 'suggestion was' : 'suggestions were'} already in the system.`
    : '';
  return `Found ${found} of the ${asked} asked for. No more turned up that fit this station and are not already in the system.${ownedText}`;
}

function addedAgo(value) {
  const at = value ? new Date(value).getTime() : NaN;
  if (!Number.isFinite(at)) return '';
  const weeks = Math.max(0, Math.floor((Date.now() - at) / (7 * 24 * 3600 * 1000)));
  if (weeks < 1) return 'added this week';
  if (weeks < 9) return `added ${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  const months = Math.round(weeks / 4.35);
  if (months < 24) return `added ${months} months ago`;
  return `added ${Math.round(months / 12)} years ago`;
}

const CURRENT_WINDOW_DAYS = 112;

// The release chip names the age of the record, and says whether that age was checked, so an
// unchecked guess never reads as a fact.
function releaseLabel(track) {
  const year = track?.released ? String(track.released) : '';
  if (!year) return '';
  if (track?.releaseVerified !== true) return `${year} · unverified`;
  const days = Number(track.releaseAgeDays);
  if (Number.isFinite(days) && days >= 0 && days <= CURRENT_WINDOW_DAYS) {
    const weeks = Math.max(1, Math.round(days / 7));
    return `${year} · ${weeks}w ago`;
  }
  const first = Number(track.firstReleased);
  if (Number.isFinite(first) && first && first !== Number(track.released)) {
    return `${year} · song from ${first}`;
  }
  return year;
}

function releaseTone(track) {
  if (track?.releaseVerified !== true) return 'default';
  const days = Number(track.releaseAgeDays);
  if (Number.isFinite(days) && days >= 0 && days <= CURRENT_WINDOW_DAYS) return 'success';
  const band = track?.ageBand;
  if (band === 'old' || band === 'recent') return 'default';
  return 'info';
}

function verifiedNote(profile) {
  const verified = Number(profile?.verified) || 0;
  const rejected = Number(profile?.rejected) || 0;
  if (!verified && !rejected) return '';
  const verifiedText = verified
    ? `${verified} ${verified === 1 ? 'release date was' : 'release dates were'} checked against the catalogue`
    : '';
  const rejectedText = rejected
    ? `${rejected} ${rejected === 1 ? 'song was' : 'songs were'} thrown out because ${rejected === 1 ? 'its real release date did' : 'their real release dates did'} not fit the categories`
    : '';
  return [verifiedText, rejectedText].filter(Boolean).join(', and ');
}

function moveLine(move) {
  return move.action === 'drop'
    ? `${move.from || 'Its category'} → out of rotation`
    : `${move.from || 'Its category'} → ${move.to}`;
}

function movePlays(move) {
  return [
    `${Number(move.spins7) || 0} plays in 7 days, ${Number(move.spins28) || 0} in 28`,
    addedAgo(move.addedAt),
  ].filter(Boolean).join(' · ');
}

function sourceLabel(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch (_error) {
    return String(url || '');
  }
}

function webSources(list) {
  return (Array.isArray(list) ? list : []).filter((url) => /^https?:\/\//i.test(String(url || '')));
}

function clampTrackCount(value, max, fallback) {
  const count = parseInt(value, 10);
  if (!Number.isFinite(count) || count < 1) return fallback;
  return Math.min(max, count);
}

function statusMeta(status) {
  if (status === 'completed') return { label: 'Done', color: 'success' };
  if (status === 'failed') return { label: 'Failed', color: 'error' };
  if (status === 'running') return { label: 'Running', color: 'info' };
  return { label: status || 'Unknown', color: 'default' };
}

function rowsForStation(rows, profile) {
  return (rows || []).filter((row) => (
    Number(row.stationId) === Number(profile?.stationId)
    || (!row.stationId && row.stationName && row.stationName === profile?.name)
  ));
}

function tracksForStation(report, profile) {
  return rowsForStation(report?.recommendations, profile);
}

function movesForStation(report, profile) {
  return rowsForStation(report?.moves, profile);
}

function reportIncludes(report) {
  const include = report?.options?.include || {};
  return { adds: include.adds !== false, review: include.review === true };
}

function capitalise(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function visibleText(value) {
  const cleaned = String(value || '')
    .replace(/\bgrok\b/gi, '')
    .replace(/\bweb searches\b/gi, '')
    .replace(/\bweb search\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([.,;:])/g, '$1')
    .trim();
  if (!cleaned) return '';
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

const PDF_BRAND = [8, 145, 178];
const PDF_INK = [15, 23, 42];
const PDF_MUTED = [100, 116, 139];
const PDF_SOFT = [241, 245, 249];
const PDF_HIGH = [217, 119, 6];
const PDF_NIGHT = [10, 12, 18];
const PDF_CYAN = [34, 211, 238];

function trackArtKey(track) {
  return `${String(track?.artist || '').trim().toLowerCase()}|${String(track?.title || '').trim().toLowerCase()}`;
}

function blobToDataUrl(blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

async function toDataUrl(url) {
  try {
    const response = await fetch(url, {
      credentials: 'include',
      signal: typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
        ? AbortSignal.timeout(12000)
        : undefined,
    });
    if (!response.ok) return null;
    return blobToDataUrl(await response.blob());
  } catch (_error) {
    return null;
  }
}

function imageFormat(dataUrl) {
  return /^data:image\/png/i.test(String(dataUrl)) ? 'PNG' : 'JPEG';
}

async function artworkDataUrls(tracks, known) {
  const result = {};
  const queue = [];
  const seen = new Set();
  for (const track of tracks) {
    const key = trackArtKey(track);
    if (!track?.title || !track?.artist || !key || key === '|' || seen.has(key)) continue;
    seen.add(key);
    queue.push(track);
  }
  const worker = async () => {
    while (queue.length) {
      const track = queue.shift();
      const key = trackArtKey(track);
      try {
        let remote = known?.[key];
        if (!remote) {
          const response = await axios.get(`${API_URL}/api/artwork/fetch`, {
            params: { title: track.title, artist: track.artist },
            withCredentials: true,
            timeout: 8000,
          });
          remote = response.data?.artwork_url || '';
        }
        if (!remote) {
          result[key] = null;
          continue;
        }
        const sized = String(remote).replace(/\/\d+x\d+bb/, '/200x200bb');
        const proxied = `${API_URL}/api/artwork/proxy?url=${encodeURIComponent(sized)}`;
        result[key] = await toDataUrl(proxied);
      } catch (_error) {
        result[key] = null;
      }
    }
  };
  await Promise.all([worker(), worker(), worker(), worker()]);
  return result;
}

function drawBars(doc, x, y, width, height) {
  const heights = [0.35, 0.62, 0.9, 0.48, 1, 0.7, 0.4, 0.82, 0.5];
  const gap = 0.8;
  const barWidth = (width - gap * (heights.length - 1)) / heights.length;
  heights.forEach((ratio, index) => {
    const barHeight = height * ratio;
    doc.setFillColor(...PDF_CYAN);
    doc.roundedRect(x + index * (barWidth + gap), y + height - barHeight, barWidth, barHeight, 0.4, 0.4, 'F');
  });
}

function drawCoverFallback(doc, x, y, size) {
  doc.setFillColor(...PDF_NIGHT);
  doc.roundedRect(x, y, size, size, 1.4, 1.4, 'F');
  drawBars(doc, x + 3, y + 6, size - 6, size - 12);
}

function drawStat(doc, x, y, value, label) {
  doc.setFillColor(22, 28, 40);
  doc.roundedRect(x, y, 26, 16, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(255, 255, 255);
  doc.text(String(value), x + 13, y + 7, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(154, 167, 184);
  doc.text(label, x + 13, y + 12.5, { align: 'center' });
}

function pdfPageFooter(doc) {
  const pages = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    doc.setFillColor(...PDF_NIGHT);
    doc.rect(0, 288, pageWidth, 9, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(154, 167, 184);
    doc.text('Suggestions are not applied to the library.', 12, 293.2);
    doc.text(`${page} / ${pages}`, pageWidth - 12, 293.2, { align: 'right' });
  }
}

function roomFor(doc, y, needed) {
  if (y + needed <= 280) return y;
  doc.addPage();
  doc.setFillColor(...PDF_CYAN);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 1.4, 'F');
  return 16;
}

function drawLines(doc, lines, lineHeight, x, y) {
  let cursor = y;
  lines.forEach((line) => {
    cursor = roomFor(doc, cursor, lineHeight + 1);
    if (line) doc.text(line, x, cursor);
    cursor += lineHeight;
  });
  return cursor;
}

function pdfLines(doc, text, size, style, width) {
  doc.setFont('helvetica', style);
  doc.setFontSize(size);
  const lines = text ? doc.splitTextToSize(String(text), width) : [];
  return { lines, lineHeight: doc.getLineHeight() / doc.internal.scaleFactor };
}

function trackCardParts(doc, track, textWidth, { tempo = false } = {}) {
  const title = pdfLines(doc, track.title || 'Untitled', 11, 'bold', textWidth);
  const artist = pdfLines(doc, track.artist || 'Unknown artist', 8.5, 'normal', textWidth);
  const metaText = [
    tempo && track.bpm ? `${track.bpm} BPM` : '',
    track.duration || '',
    track.released ? releaseLabel(track) : '',
    track.category || '',
    track.priority === 'high' ? 'High' : '',
  ].filter(Boolean).join('   ·   ');
  const meta = pdfLines(doc, metaText, 7.5, 'normal', textWidth);
  const reason = pdfLines(doc, visibleText(track.reason || ''), 8, 'normal', textWidth);
  const gap = 1.15;
  let block = 6.5;
  block += title.lines.length * title.lineHeight;
  if (artist.lines.length) block += gap + artist.lines.length * artist.lineHeight;
  if (meta.lines.length) block += gap + meta.lines.length * meta.lineHeight;
  if (reason.lines.length) block += gap + reason.lines.length * reason.lineHeight;
  block += 4;
  return { title, artist, meta, reason, height: Math.max(32, block) };
}

function drawWrapped(doc, lines, lineHeight, x, y) {
  if (!lines.length) return y;
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
}

function drawTrackCard(doc, track, cover, x, y, width, tempo = false) {
  const textX = x + 32;
  const textWidth = width - 38;
  const { title, artist, meta, reason, height } = trackCardParts(doc, track, textWidth, { tempo });
  doc.setFillColor(...PDF_SOFT);
  doc.roundedRect(x, y, width, height, 2, 2, 'F');
  doc.setFillColor(...(track.priority === 'high' ? PDF_HIGH : PDF_BRAND));
  doc.rect(x, y + 2, 1.3, height - 4, 'F');
  if (cover) {
    try {
      doc.addImage(cover, imageFormat(cover), x + 5, y + 5, 22, 22);
    } catch (_error) {
      drawCoverFallback(doc, x + 5, y + 5, 22);
    }
  } else {
    drawCoverFallback(doc, x + 5, y + 5, 22);
  }
  let cursor = y + 7;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...PDF_INK);
  cursor = drawWrapped(doc, title.lines, title.lineHeight, textX, cursor) + 1.15;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...PDF_MUTED);
  cursor = drawWrapped(doc, artist.lines, artist.lineHeight, textX, cursor);
  if (meta.lines.length) {
    cursor += 1.15;
    doc.setFontSize(7.5);
    doc.setTextColor(...PDF_BRAND);
    cursor = drawWrapped(doc, meta.lines, meta.lineHeight, textX, cursor);
  }
  if (reason.lines.length) {
    cursor += 1.15;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(51, 65, 85);
    drawWrapped(doc, reason.lines, reason.lineHeight, textX, cursor);
  }
  return height;
}

function moveCardParts(doc, move, textWidth) {
  const heading = pdfLines(doc, `${MOVE_LABELS[move.direction] || 'Move'}: ${move.title || 'Untitled'}`, 10.5, 'bold', textWidth);
  const artist = pdfLines(doc, move.artist || 'Unknown artist', 8.5, 'normal', textWidth);
  const routeText = move.action === 'drop'
    ? `Take out of ${move.from || 'its category'}`
    : `From ${move.from || 'its category'} to ${move.to}`;
  const route = pdfLines(doc, routeText, 8, 'bold', textWidth);
  const plays = pdfLines(doc, movePlays(move), 7.5, 'normal', textWidth);
  const reason = pdfLines(doc, visibleText(move.reason || ''), 8, 'normal', textWidth);
  let block = 6;
  block += heading.lines.length * heading.lineHeight;
  [artist, route, plays, reason].forEach((part) => {
    if (part.lines.length) block += 1.15 + part.lines.length * part.lineHeight;
  });
  block += 3.5;
  return { heading, artist, route, plays, reason, height: block };
}

function drawMoveCard(doc, move, x, y, width) {
  const textX = x + 6;
  const parts = moveCardParts(doc, move, width - 10);
  doc.setFillColor(...PDF_SOFT);
  doc.roundedRect(x, y, width, parts.height, 2, 2, 'F');
  doc.setFillColor(...(move.action === 'drop' ? [220, 38, 38] : PDF_BRAND));
  doc.rect(x, y + 2, 1.3, parts.height - 4, 'F');
  let cursor = y + 6.5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...PDF_INK);
  cursor = drawWrapped(doc, parts.heading.lines, parts.heading.lineHeight, textX, cursor);
  const sections = [
    [parts.artist, 'normal', 8.5, PDF_MUTED],
    [parts.route, 'bold', 8, PDF_BRAND],
    [parts.plays, 'normal', 7.5, PDF_MUTED],
    [parts.reason, 'normal', 8, [51, 65, 85]],
  ];
  sections.forEach(([part, style, size, color]) => {
    if (!part.lines.length) return;
    cursor += 1.15;
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
    cursor = drawWrapped(doc, part.lines, part.lineHeight, textX, cursor);
  });
  return parts.height;
}

async function downloadReportPdf(report, knownArt = {}) {
  const doc = new jsPDF({ orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 12;
  const contentWidth = pageWidth - margin * 2;
  const logo = await toDataUrl(radioSuiteLogo);
  const covers = await artworkDataUrls(report.recommendations || [], knownArt);

  doc.setFillColor(...PDF_NIGHT);
  doc.rect(0, 0, pageWidth, 48, 'F');
  doc.setFillColor(...PDF_CYAN);
  doc.rect(0, 0, pageWidth, 1.6, 'F');
  if (logo) {
    try {
      doc.addImage(logo, imageFormat(logo), margin, 10, 28, 28);
    } catch (_error) {
      drawBars(doc, margin, 18, 22, 12);
    }
  } else {
    drawBars(doc, margin, 18, 22, 12);
  }
  const textX = margin + 34;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...PDF_CYAN);
  doc.text('MUSIC INTELLIGENCE', textX, 18);
  doc.setFontSize(18);
  doc.setTextColor(255, 255, 255);
  const title = report.weekday ? `${report.weekday} report` : 'Intelligence report';
  doc.text(doc.splitTextToSize(title, 90)[0], textX, 27);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(154, 167, 184);
  doc.text(formatWhen(report.createdAt) || '', textX, 34);

  const totalTracks = (report.recommendations || []).length;
  const totalMoves = (report.moves || []).length;
  const stationCount = (report.profiles || []).length || (report.stationIds || []).length;
  const parts = reportIncludes(report);
  const stats = [
    parts.adds ? [totalTracks, totalTracks === 1 ? 'track' : 'tracks'] : null,
    parts.review ? [totalMoves, totalMoves === 1 ? 'move' : 'moves'] : null,
    [stationCount, stationCount === 1 ? 'station' : 'stations'],
  ].filter(Boolean);
  stats.forEach(([value, label], index) => {
    drawStat(doc, pageWidth - margin - 28 - index * 30, 16, value, label);
  });

  let y = 58;
  const profiles = report.profiles || [];
  const sections = profiles.length > 0
    ? profiles
    : [{ name: 'Suggestions', recommendations: report.recommendations || [] }];

  sections.forEach((profile) => {
    const tracks = profile.recommendations || tracksForStation(report, profile);
    const summaryText = visibleText(profile.summary);
    const focusText = visibleText(profile.focus);
    const note = profile.error
      ? visibleText(profile.error)
      : [summaryText, focusText && focusText !== summaryText ? focusText : ''].filter(Boolean).join('\n\n');
    const market = [profile.location, profile.country].filter(Boolean).join(', ');
    const stationName = profile.name || 'Station';
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const formatLabel = profile.format ? String(profile.format).slice(0, 24) : '';
    const pillWidth = formatLabel ? doc.getTextWidth(formatLabel) + 8 : 0;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    const nameWidth = Math.max(40, contentWidth - pillWidth - 6);
    const nameLines = doc.splitTextToSize(stationName, nameWidth);
    const nameLineHeight = doc.getLineHeight() / doc.internal.scaleFactor;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const summaryLines = note ? doc.splitTextToSize(note, contentWidth) : [];
    const summaryLineHeight = doc.getLineHeight() / doc.internal.scaleFactor;
    const headingHeight = nameLines.length * nameLineHeight + (market ? 6 : 2);
    y = roomFor(doc, y, headingHeight + 8);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...PDF_INK);
    doc.text(nameLines, margin, y);
    if (formatLabel) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setFillColor(...PDF_BRAND);
      doc.roundedRect(pageWidth - margin - pillWidth, y - 5, pillWidth, 7, 1.5, 1.5, 'F');
      doc.setTextColor(255, 255, 255);
      doc.text(formatLabel, pageWidth - margin - pillWidth + 4, y - 0.6);
    }
    y += nameLines.length * nameLineHeight + 1.5;
    if (market) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...PDF_MUTED);
      doc.text(market, margin, y);
      y += 5;
    }
    if (summaryLines.length) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(51, 65, 85);
      y = drawLines(doc, summaryLines, summaryLineHeight, margin, y);
    }
    if (profile.categories?.length) {
      const looked = pdfLines(doc, `Categories looked at: ${profile.categories.join(', ')}`, 8, 'normal', contentWidth);
      doc.setTextColor(...PDF_MUTED);
      y = drawLines(doc, looked.lines, looked.lineHeight, margin, y + 1);
    }
    if (verifiedNote(profile)) {
      const checked = pdfLines(doc, `${verifiedNote(profile)}.`, 8, 'normal', contentWidth);
      doc.setTextColor(...PDF_MUTED);
      y = drawLines(doc, checked.lines, checked.lineHeight, margin, y + 1);
    }
    y += 3;
    if (parts.adds && !tracks.length) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(...PDF_MUTED);
      doc.text('No new tracks to add.', margin, y);
      y += 10;
    }
    const showTempo = profile.beatmix === true;
    tracks.forEach((track) => {
      const { height: cardHeight } = trackCardParts(doc, track, contentWidth - 38, { tempo: showTempo });
      y = roomFor(doc, y, cardHeight + 3);
      const height = drawTrackCard(doc, track, covers[trackArtKey(track)], margin, y, contentWidth, showTempo);
      y += height + 3;
    });
    if (parts.review && !profile.error) {
      const moves = movesForStation(report, profile);
      y = roomFor(doc, y + 2, 16);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...PDF_INK);
      doc.text(`Moves and drops (${moves.length})`, margin, y);
      y += 5;
      const reviewNote = visibleText(profile.reviewError || profile.reviewSummary);
      if (reviewNote) {
        const noteLines = pdfLines(doc, reviewNote, 9, 'normal', contentWidth);
        doc.setTextColor(51, 65, 85);
        y = drawLines(doc, noteLines.lines, noteLines.lineHeight, margin, y) + 2;
      }
      if (!moves.length && !profile.reviewError) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(9);
        doc.setTextColor(...PDF_MUTED);
        doc.text('No moves suggested.', margin, y);
        y += 8;
      }
      moves.forEach((move) => {
        const { height: cardHeight } = moveCardParts(doc, move, contentWidth - 10);
        y = roomFor(doc, y, cardHeight + 3);
        y += drawMoveCard(doc, move, margin, y, contentWidth) + 3;
      });
    }
    y += 4;
  });

  pdfPageFooter(doc);
  doc.save(`intelligence-report-${report.id}.pdf`);
}

function readAnswer(answer) {
  const text = String(answer || '').trim();
  if (text.startsWith('{') || text.startsWith('```')) {
    const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
    const body = fenced ? fenced[1].trim() : text;
    try {
      const parsed = JSON.parse(body);
      if (parsed && (parsed.lead || parsed.tracks || parsed.answer || parsed.moves)) {
        return {
          lead: visibleText(parsed.lead || parsed.answer || ''),
          tracks: Array.isArray(parsed.tracks) ? parsed.tracks : [],
          moves: Array.isArray(parsed.moves) ? parsed.moves : [],
          sources: webSources(parsed.sources),
        };
      }
    } catch (_error) {
      const lead = body.match(/"lead"\s*:\s*"((?:\\.|[^"\\])*)"/);
      if (lead) {
        try {
          return { lead: visibleText(JSON.parse(`"${lead[1]}"`)), tracks: [], moves: [], sources: [] };
        } catch (_inner) {
          return { lead: visibleText(lead[1]), tracks: [], moves: [], sources: [] };
        }
      }
    }
  }
  return { lead: visibleText(text), tracks: [], moves: [], sources: [] };
}

const MOVE_ASK = /\b(move|moves|moving|drop|drops|remove|cull|demote|promote|retire|burnt|burned|tired|stale|rest(?:ing)? (?:these|them|tracks?|songs?))\b|\bout of (?:power|current|main|rotation)\b/i;
const SEARCH_ASK = /\b(search|look\s*(?:it\s*)?up|lookup|google|online|on the (?:web|internet)|internet|website|latest|news|trending|charting|chart positions?|this week'?s (?:charts?|releases?))\b/i;

function moveAsk(text) {
  return MOVE_ASK.test(String(text || '').replace(/\b(new drops?|just dropped|dropped (?:this|last) (?:week|month|friday))\b/gi, ' '));
}

function trackAsk(text) {
  const value = String(text || '');
  const wants = /\b(current releases?|new (?:music|tracks?|songs?|records?|releases?))\b/i.test(value)
    || (/\b(tracks?|songs?|records?)\b/i.test(value) && /\b(add|recommend|suggest|missing)\b/i.test(value));
  return wants && (!moveAsk(value) || /\b(add|missing|new)\b/i.test(value));
}

function searchAsk(text) {
  return SEARCH_ASK.test(String(text || ''));
}

function askStepsFor(text, search) {
  const steps = [
    'Reading clocks, categories, and spins',
    'Checking who is already rotating',
  ];
  if (moveAsk(text)) {
    steps.push('Listing each category\'s tracks and plays', 'Weighing plays, age, and category roles');
  }
  if (trackAsk(text)) {
    steps.push('Matching the format, sound, and clock length');
  }
  if (search) steps.push('Searching online');
  steps.push(moveAsk(text) ? 'Writing the moves' : trackAsk(text) ? 'Writing the tracks' : 'Writing the answer');
  return steps;
}

const trackGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
  alignItems: 'start',
  gap: 1.5,
};

function useStudioSurface() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.brand?.accent || theme.palette.info.main;
  return { theme, isDark, accent };
}

function QuotaPips({ used, total }) {
  const count = Math.max(1, Number(total) || 1);
  const filled = Math.min(count, Math.max(0, Number(used) || 0));
  return (
    <Box sx={{ display: 'flex', gap: 0.45, justifyContent: 'flex-end', mt: 0.55 }} aria-hidden>
      {Array.from({ length: count }, (_, index) => (
        <Box
          key={index}
          sx={{
            width: 16,
            height: 5,
            borderRadius: 99,
            bgcolor: index < filled ? 'info.main' : 'action.disabledBackground',
            boxShadow: index < filled ? '0 0 10px rgba(34,211,238,0.55)' : 'none',
          }}
        />
      ))}
    </Box>
  );
}

function ModeCard({ value, selected, icon, title, detail, busy, onSelect }) {
  return (
    <ButtonBase
      id={`intelligence-tab-${value}`}
      role="tab"
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      focusRipple
      onClick={() => onSelect(value)}
      sx={{
        flex: { xs: 1, sm: '0 1 300px' },
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 1.25,
        px: 1.5,
        py: 1.1,
        borderRadius: '14px',
        border: '1px solid',
        borderColor: selected ? 'info.main' : 'divider',
        bgcolor: (theme) => (selected ? alpha(theme.palette.info.main, 0.1) : theme.palette.action.hover),
        boxShadow: (theme) => (selected
          ? `0 0 0 1px ${alpha(theme.palette.info.main, 0.25)}, 0 14px 30px -20px ${alpha(theme.palette.info.main, 0.9)}`
          : 'none'),
        textAlign: 'left',
        transition: 'border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease',
        '&:hover': {
          borderColor: selected ? 'info.main' : 'text.secondary',
          bgcolor: (theme) => (selected ? alpha(theme.palette.info.main, 0.14) : theme.palette.action.selected),
        },
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '10px',
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          color: selected ? 'info.contrastText' : 'info.main',
          bgcolor: (theme) => (selected ? theme.palette.info.main : alpha(theme.palette.info.main, 0.14)),
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.25 }} noWrap>{title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }} noWrap>
          {detail}
        </Typography>
      </Box>
      {busy && <CircularProgress size={14} color="info" sx={{ flexShrink: 0 }} />}
    </ButtonBase>
  );
}

function WaveformMark() {
  const { accent } = useStudioSurface();
  const heights = [28, 48, 78, 42, 100, 64, 34, 86, 50];
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px', px: 1.25 }}>
      {heights.map((height, index) => (
        <Box
          key={height + index}
          sx={{
            width: 3,
            height: `${height}%`,
            maxHeight: '70%',
            borderRadius: 99,
            bgcolor: accent,
            opacity: 0.35 + (index % 3) * 0.18,
          }}
        />
      ))}
    </Box>
  );
}

function CoverArt({ url }) {
  const { isDark } = useStudioSurface();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [url]);
  const showImage = Boolean(url) && !failed;
  return (
    <Box
      sx={{
        width: 84,
        height: 84,
        borderRadius: '12px',
        overflow: 'hidden',
        flexShrink: 0,
        bgcolor: isDark ? '#0c1018' : '#e8eef5',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: isDark ? '0 10px 24px -16px rgba(0,0,0,0.8)' : '0 10px 24px -18px rgba(15,23,42,0.35)',
      }}
    >
      {showImage ? (
        <Box
          component="img"
          src={url}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <WaveformMark />
      )}
    </Box>
  );
}

function TrackSuggestion({ track, index, artUrl, showTempo = false }) {
  const { isDark, accent } = useStudioSurface();
  const reason = visibleText(track.reason);
  const high = track.priority === 'high';
  const stripe = high ? (isDark ? '#f59e0b' : '#d97706') : accent;
  return (
    <Box
      sx={{
        position: 'relative',
        p: 2,
        pl: 2.25,
        borderRadius: '14px',
        border: '1px solid',
        borderColor: isDark ? 'rgba(148,163,184,0.16)' : 'rgba(15,23,42,0.08)',
        background: isDark
          ? 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(22,27,37,0.55) 42%)'
          : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        boxShadow: isDark
          ? '0 1px 0 rgba(255,255,255,0.04) inset, 0 18px 40px -30px rgba(0,0,0,0.85)'
          : '0 1px 0 rgba(255,255,255,0.9) inset, 0 16px 36px -28px rgba(15,23,42,0.28)',
        minWidth: 0,
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 12,
          bottom: 12,
          width: 3,
          borderRadius: 99,
          background: stripe,
          boxShadow: high ? '0 0 12px rgba(245,158,11,0.65)' : `0 0 12px ${accent}88`,
        },
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
        <CoverArt url={artUrl} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
            {String(index + 1).padStart(2, '0')}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.01em', overflowWrap: 'anywhere' }}>
            {track.title || 'Untitled'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.15, overflowWrap: 'anywhere' }}>
            {track.artist || 'Unknown artist'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.15 }}>
            {high && <Chip size="small" color="warning" label="High" />}
            {track.priority === 'low' && <Chip size="small" variant="outlined" label="Low" />}
            {showTempo && track.bpm ? <Chip size="small" variant="outlined" label={`${track.bpm} BPM`} /> : null}
            {track.duration ? <Chip size="small" variant="outlined" label={track.duration} /> : null}
            {track.released ? (
              <Chip
                size="small"
                variant="outlined"
                color={releaseTone(track)}
                label={releaseLabel(track)}
                title={track.releaseVerified
                  ? `Release date checked against the catalogue${track.firstReleased ? ` · first released ${track.firstReleased}` : ''}`
                  : 'Release year given by Intelligence and not checked'}
              />
            ) : null}
            {track.station ? <Chip size="small" variant="outlined" label={track.station} /> : null}
            {track.category ? <Chip size="small" color="info" variant="outlined" label={track.category} sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere' } }} /> : null}
          </Box>
        </Box>
      </Box>
      {reason && (
        <Typography variant="body2" sx={{ mt: 1.35, lineHeight: 1.65, color: 'text.secondary', overflowWrap: 'anywhere' }}>
          {reason}
        </Typography>
      )}
      {track.source && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, overflowWrap: 'anywhere' }}>
          Source: {visibleText(track.source)}
        </Typography>
      )}
    </Box>
  );
}

function MoveSuggestion({ move, index }) {
  const { isDark } = useStudioSurface();
  const drop = move.action === 'drop';
  const tone = drop ? 'error' : move.direction === 'promote' ? 'success' : move.direction === 'demote' ? 'warning' : 'info';
  const reason = visibleText(move.reason);
  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: '14px',
        border: '1px solid',
        borderColor: isDark ? 'rgba(148,163,184,0.16)' : 'rgba(15,23,42,0.08)',
        borderLeft: '3px solid',
        borderLeftColor: `${tone}.main`,
        background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>
          {String(index + 1).padStart(2, '0')}
        </Typography>
        <Chip size="small" color={tone} label={MOVE_LABELS[move.direction] || (drop ? 'Drop' : 'Move')} />
        {move.priority === 'high' && <Chip size="small" variant="outlined" color="warning" label="High" />}
        {move.station && <Chip size="small" variant="outlined" label={move.station} />}
      </Box>
      <Typography variant="subtitle1" sx={{ mt: 0.75, fontWeight: 700, lineHeight: 1.25, overflowWrap: 'anywhere' }}>
        {move.title || 'Untitled'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
        {move.artist || 'Unknown artist'}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 600, overflowWrap: 'anywhere' }}>
        {moveLine(move)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {movePlays(move)}
      </Typography>
      {reason && (
        <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.6, color: 'text.secondary', overflowWrap: 'anywhere' }}>
          {reason}
        </Typography>
      )}
    </Box>
  );
}

function SourceLinks({ sources }) {
  const list = webSources(sources);
  if (!list.length) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="overline" color="text.secondary">Sources</Typography>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.25 }}>
        {list.map((url) => (
          <Chip
            key={url}
            size="small"
            variant="outlined"
            clickable
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={url}
            label={sourceLabel(url)}
          />
        ))}
      </Box>
    </Box>
  );
}

function categoryPreset(picked, categories, clockIds) {
  if (picked.size === 0) return 'none';
  if (clockIds.length && picked.size === clockIds.length && clockIds.every((id) => picked.has(id))) return 'clock';
  if (picked.size === categories.length) return 'all';
  return null;
}

function CategoryPicker({ station, info, chosen, onChange, disabled }) {
  const categories = info?.categories || [];
  const picked = new Set(chosen || []);
  // With no clock scheduling a category, the default ticks are just the biggest categories, not the clock's.
  const clockIds = categories.filter((category) => category.defaultSelected && category.inClock).map((category) => category.id);
  const preset = categoryPreset(picked, categories, clockIds);
  const toggle = (id) => {
    onChange(picked.has(id) ? [...picked].filter((value) => value !== id) : [...picked, id]);
  };
  const applyPreset = (_event, value) => {
    if (value === 'clock') onChange(clockIds);
    else if (value === 'all') onChange(categories.map((category) => category.id));
    else if (value === 'none') onChange([]);
  };
  return (
    <Box sx={{ mb: 1.5, borderRadius: '14px', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>{station.name}</Typography>
          {info?.format && <Chip size="small" color="info" variant="outlined" label={info.format} sx={{ flexShrink: 0 }} />}
        </Box>
        {info?.formatEvidence?.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            title={`Format read from ${info.formatEvidence.join(', ')}`}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.4,
              mt: 0.25,
            }}
          >
            Format read from {info.formatEvidence.slice(0, 3).join(', ')}
          </Typography>
        )}
      </Box>
      {!info ? (
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <CircularProgress size={16} />
        </Box>
      ) : categories.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, pb: 1.5 }}>
          {info.failed
            ? 'Could not load the categories, so the report uses the clock\'s. Untick and tick the station to try again.'
            : 'No music categories are assigned to this station.'}
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1.5, pb: 1 }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={preset}
              onChange={applyPreset}
              disabled={disabled}
              aria-label={`Quick pick for ${station.name}`}
              sx={{
                '& .MuiToggleButton-root': {
                  px: 1.25,
                  py: 0.2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  lineHeight: 1.6,
                },
                '& .MuiToggleButton-root.Mui-selected': {
                  color: 'info.main',
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.14),
                },
              }}
            >
              <ToggleButton
                value="clock"
                disabled={disabled || clockIds.length === 0}
                title={clockIds.length ? 'Tick the categories the station\'s clocks play' : 'No clock plays any of these categories'}
              >
                Clock
              </ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="none">None</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
              {picked.size} of {categories.length} ticked
            </Typography>
          </Box>
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', maxHeight: 420, overflow: 'auto', p: 0.5 }}>
            {categories.map((category) => {
              const checked = picked.has(category.id);
              const meta = [category.roleLabel, category.inClock ? 'In clock' : ''].filter(Boolean).join(' · ');
              return (
                <Box
                  key={category.id}
                  component="label"
                  title={`${category.name} · ${category.count} ${category.count === 1 ? 'song' : 'songs'}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.75,
                    px: 0.75,
                    py: 0.5,
                    borderRadius: '10px',
                    cursor: disabled ? 'default' : 'pointer',
                    bgcolor: (theme) => (checked ? alpha(theme.palette.info.main, 0.1) : 'transparent'),
                    transition: 'background-color 120ms ease',
                    '&:hover': {
                      bgcolor: (theme) => (checked ? alpha(theme.palette.info.main, 0.14) : theme.palette.action.hover),
                    },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggle(category.id)}
                    sx={{ p: 0.4, mt: '-1px' }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0, py: 0.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: checked ? 600 : 500, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                      {category.name}
                    </Typography>
                    {meta && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>
                        {meta}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      flexShrink: 0,
                      pt: 0.35,
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                      color: category.count ? 'text.secondary' : 'text.disabled',
                    }}
                  >
                    {category.count}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          {picked.size === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              {clockIds.length
                ? 'None ticked, so the report uses the clock\'s categories.'
                : 'None ticked, so the report uses the biggest categories.'}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

function useArtworkMap(tracks) {
  const [art, setArt] = useState({});
  const artRef = useRef({});
  const pending = useRef(new Set());
  const signature = tracks.map((track) => trackArtKey(track)).join('\n');
  useEffect(() => {
    const queue = [];
    const seen = new Set();
    for (const track of tracks) {
      const key = trackArtKey(track);
      if (!track?.title || !track?.artist || !key || key === '|' || seen.has(key)) continue;
      if (artRef.current[key] !== undefined || pending.current.has(key)) continue;
      seen.add(key);
      pending.current.add(key);
      queue.push(track);
    }
    if (!queue.length) return undefined;
    const run = async () => {
      while (queue.length) {
        const batch = queue.splice(0, 3);
        await Promise.all(batch.map(async (track) => {
          const key = trackArtKey(track);
          let url = '';
          try {
            const response = await axios.get(`${API_URL}/api/artwork/fetch`, {
              params: { title: track.title, artist: track.artist },
              withCredentials: true,
              timeout: 8000,
            });
            url = response.data?.artwork_url || '';
          } catch (_error) {
            url = '';
          }
          artRef.current[key] = url;
          pending.current.delete(key);
        }));
        setArt({ ...artRef.current });
      }
    };
    run();
    return undefined;
  }, [signature, tracks]);
  return art;
}

function AgentTrace({ steps, step }) {
  const { isDark, accent } = useStudioSurface();
  const current = Math.min(step, Math.max(0, steps.length - 1));
  return (
    <Box
      sx={{
        mb: 2.5,
        p: 2,
        borderRadius: '14px',
        border: '1px solid',
        borderColor: isDark ? 'rgba(34,211,238,0.35)' : 'rgba(8,145,178,0.28)',
        background: isDark
          ? 'linear-gradient(120deg, rgba(34,211,238,0.08), rgba(22,27,37,0.4) 46%)'
          : 'linear-gradient(120deg, rgba(8,145,178,0.08), rgba(255,255,255,0.8) 50%)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <CircularProgress size={14} sx={{ color: accent }} />
        <Typography variant="body2" sx={{ fontWeight: 700, letterSpacing: '0.04em' }}>Working</Typography>
      </Box>
      <Box sx={{ display: 'grid', gap: 0.35 }}>
        {steps.map((label, index) => {
          const state = index < current ? 'done' : index === current ? 'now' : 'next';
          return (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.35 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: state === 'next' ? 'action.disabledBackground' : accent,
                  boxShadow: state === 'now' ? `0 0 0 4px ${isDark ? 'rgba(34,211,238,0.18)' : 'rgba(8,145,178,0.16)'}` : 'none',
                }}
              />
              <Typography
                variant="body2"
                color={state === 'next' ? 'text.disabled' : 'text.primary'}
                sx={{ fontWeight: state === 'now' ? 700 : 400 }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default function Intelligence() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [stations, setStations] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [status, setStatus] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [question, setQuestion] = useState('');
  const [searchQuestion, setSearchQuestion] = useState(false);
  const [asking, setAsking] = useState(false);
  const [focusQuestion, setFocusQuestion] = useState(false);
  const [agentStep, setAgentStep] = useState(0);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [trackCountInput, setTrackCountInput] = useState('');
  const [includeAdds, setIncludeAdds] = useState(true);
  const [includeMoves, setIncludeMoves] = useState(false);
  const [categoryInfo, setCategoryInfo] = useState({});
  const [categoryChoice, setCategoryChoice] = useState({});
  const [view, setView] = useState('reports');
  const [clock, setClock] = useState(() => Date.now());
  const threadRef = useRef(null);
  const askedRef = useRef('');
  const requestedCategories = useRef(new Set());

  const load = useCallback(async () => {
    const [stationRes, statusRes, reportRes, questionRes] = await Promise.all([
      axios.get(`${API_URL}/api/stations`, { withCredentials: true }),
      axios.get(`${API_URL}/api/intelligence/status`, { withCredentials: true }),
      axios.get(`${API_URL}/api/intelligence/reports`, { withCredentials: true }),
      axios.get(`${API_URL}/api/intelligence/questions`, { withCredentials: true }),
    ]);
    const allStations = Array.isArray(stationRes.data) ? stationRes.data : [];
    const allowedIds = Array.isArray(statusRes.data?.allowedStationIds)
      ? new Set(statusRes.data.allowedStationIds.map(Number))
      : null;
    const visibleStations = allowedIds
      ? allStations.filter((station) => allowedIds.has(Number(station.id)))
      : allStations;
    setStations(visibleStations);
    if (visibleStations.length === 1) {
      const onlyId = visibleStations[0].id;
      setSelectedIds((current) => (
        current.length === 1 && current[0] === onlyId ? current : [onlyId]
      ));
    }
    setStatus(statusRes.data);
    setReports(Array.isArray(reportRes.data) ? reportRes.data : []);
    setQuestions(Array.isArray(questionRes.data) ? questionRes.data : []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    load()
      .catch((error) => {
        if (!cancelled) {
          setMessage({ type: 'error', text: error.response?.data?.message || 'Could not load Intelligence.' });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    if (view !== 'reports') return;
    const missing = selectedIds.map(Number).filter((id) => !requestedCategories.current.has(id));
    if (!missing.length) return;
    missing.forEach((id) => requestedCategories.current.add(id));
    axios.get(`${API_URL}/api/intelligence/categories`, {
      params: { stationIds: missing.join(',') },
      withCredentials: true,
    })
      .then((response) => {
        const rows = Array.isArray(response.data?.stations) ? response.data.stations : [];
        setCategoryInfo((current) => {
          const next = { ...current };
          missing.forEach((id) => {
            next[id] = { stationId: id, categories: [] };
          });
          rows.forEach((row) => {
            next[row.stationId] = row;
          });
          return next;
        });
        setCategoryChoice((current) => {
          const next = { ...current };
          rows.forEach((row) => {
            if (!next[row.stationId]) {
              next[row.stationId] = (row.categories || []).filter((category) => category.defaultSelected).map((category) => category.id);
            }
          });
          return next;
        });
      })
      .catch((error) => {
        missing.forEach((id) => requestedCategories.current.delete(id));
        setCategoryInfo((current) => {
          const next = { ...current };
          missing.forEach((id) => {
            next[id] = { stationId: id, categories: [], failed: true };
          });
          return next;
        });
        setMessage({ type: 'error', text: error.response?.data?.message || 'Could not load the station categories.' });
      });
  }, [selectedIds, view]);

  const reportRetentionDays = status?.limits?.reportRetentionDays || 30;
  const ownReportRunning = reports.some((report) => report.status === 'running');
  const reportInProgress = ownReportRunning || Boolean(status?.limits?.running);
  const questionInProgress = Boolean(status?.questions?.running);
  const reportRunning = reportInProgress || (!asking && questionInProgress);
  const questionCoolingDown = /wait a few seconds/i.test(status?.questions?.blockReason || '');
  useEffect(() => {
    if (!questionCoolingDown) return undefined;
    const timer = setTimeout(() => {
      load().catch(() => {});
    }, 16000);
    return () => clearTimeout(timer);
  }, [questionCoolingDown, load]);

  const openReportId = detailOpen && activeReport?.status === 'running' ? activeReport.id : null;
  useEffect(() => {
    if (!reportRunning && !openReportId) return undefined;
    const timer = setInterval(() => {
      load().catch(() => {});
      if (openReportId) {
        axios.get(`${API_URL}/api/intelligence/reports/${openReportId}`, { withCredentials: true })
          .then((response) => setActiveReport(response.data))
          .catch(() => {});
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [reportRunning, openReportId, load]);

  const nextRunAt = status?.limits?.nextRunAt;
  useEffect(() => {
    if (!nextRunAt) return undefined;
    const waitMs = new Date(nextRunAt).getTime() - Date.now();
    if (!Number.isFinite(waitMs)) return undefined;
    // A browser clock ahead of the server reloads before the cooldown ends and
    // gets the same nextRunAt back; the floor keeps those retries spaced out.
    const timer = setTimeout(() => {
      load().catch(() => {});
    }, Math.max(15000, waitMs + 1000));
    return () => clearTimeout(timer);
  }, [status, nextRunAt, load]);

  useEffect(() => {
    if (!nextRunAt) return undefined;
    setClock(Date.now());
    const timer = setInterval(() => setClock(Date.now()), 15000);
    return () => clearInterval(timer);
  }, [nextRunAt]);

  const selectedStations = useMemo(
    () => stations.filter((station) => selectedIds.includes(station.id)),
    [stations, selectedIds]
  );
  const missingCountry = selectedStations.filter((station) => !station.country);
  const askScope = focusQuestion && selectedStations.length ? selectedStations : stations;
  const askBeatmix = askScope.length > 0 && askScope.every((station) => station.beatmix_enabled === true);
  const runsToday = status?.limits?.runsToday || 0;
  const runsAllowed = status?.limits?.maxCompletedRunsPerDay || 5;
  const maxTracks = status?.limits?.maxTracks || 100;
  const defaultTracks = status?.limits?.defaultTracks || 20;
  const trackCount = clampTrackCount(trackCountInput, maxTracks, defaultTracks);

  const toggleStation = (stationId) => {
    setSelectedIds((current) => (
      current.includes(stationId)
        ? current.filter((id) => id !== stationId)
        : [...current, stationId]
    ));
  };

  const openReport = async (report) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/intelligence/reports/${report.id}`, { withCredentials: true });
      setActiveReport(response.data);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Could not open that report.' });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadReport = async (report) => {
    setPdfBusy(true);
    try {
      const loaded = report?.id === activeReport?.id && activeReport?.status === 'completed';
      const response = loaded
        ? { data: activeReport }
        : await axios.get(`${API_URL}/api/intelligence/reports/${report.id}`, { withCredentials: true });
      await downloadReportPdf(response.data, artworkRef.current);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Could not download the PDF.' });
    } finally {
      setPdfBusy(false);
    }
  };

  const askQuestion = async () => {
    const text = question.trim();
    if (!text) return;
    if (focusQuestion && selectedIds.length === 0) {
      setMessage({ type: 'info', text: 'Tick at least one station, or turn focus off to ask about every station.' });
      return;
    }
    askedRef.current = text;
    setAsking(true);
    setMessage(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/intelligence/questions`,
        { question: text, stationIds: focusQuestion ? selectedIds : [], search: searchQuestion || trackAsk(text) || searchAsk(text) },
        { withCredentials: true }
      );
      setQuestion('');
      setQuestions((current) => [response.data, ...current.filter((item) => item.id !== response.data.id)]);
      await load().catch(() => {});
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Could not answer that question.' });
      await load().catch(() => {});
    } finally {
      setAsking(false);
    }
  };

  const runReport = async () => {
    setStarting(true);
    setMessage(null);
    try {
      const categoryIds = {};
      selectedIds.forEach((id) => {
        const chosen = categoryChoice[id] || [];
        if (chosen.length) categoryIds[id] = chosen;
      });
      const response = await axios.post(
        `${API_URL}/api/intelligence/reports`,
        { stationIds: selectedIds, trackCount, categoryIds, include: { adds: includeAdds, review: includeMoves } },
        { withCredentials: true }
      );
      setActiveReport(response.data);
      setDetailOpen(true);
      setView('reports');
      const addsText = `${trackCount} ${trackCount === 1 ? 'track' : 'tracks'} to add per station`;
      setMessage({
        type: 'success',
        text: includeAdds && includeMoves
          ? `Report started for ${addsText}, plus moves and drops. Results appear as they are found.`
          : includeAdds
            ? `Report started for ${addsText}. Tracks appear as they are found.`
            : 'Report started for moves and drops in the chosen categories.',
      });
      await load().catch(() => {});
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Could not start the report.' });
    } finally {
      setStarting(false);
    }
  };

  const runBlocked = !status?.aiReady || !status?.limits?.canRun || selectedIds.length === 0 || starting || (!includeAdds && !includeMoves);
  const blockText = status?.limits?.blockReason
    || (!loading && stations.length === 0 ? 'No stations yet.' : null)
    || (selectedIds.length === 0 && stations.length > 1 ? 'Select at least one station.' : null);
  const questionStats = status?.questions;
  const selectionLabel = selectedIds.length === 0
    ? 'None selected'
    : `${selectedIds.length} selected`;
  const cooldownMinutes = status?.limits?.cooldownMinutes || 30;
  const cooldownLeftMs = nextRunAt ? new Date(nextRunAt).getTime() - clock : 0;
  const coolingDown = Boolean(nextRunAt) && Number.isFinite(cooldownLeftMs) && status?.limits?.canRun === false;
  const dailyLimitHit = runsToday >= runsAllowed;
  let runLabel = 'Run report';
  let runNote = status
    ? (blockText || `${runsAllowed - runsToday} of ${runsAllowed} reports left today · ${cooldownMinutes} min between runs`)
    : '';
  if (starting) {
    runLabel = 'Starting';
  } else if (ownReportRunning) {
    runLabel = 'Report running';
    runNote = 'Open it from the list below. Tracks appear as each station finishes.';
  } else if (reportInProgress) {
    runLabel = 'Another report is running';
    runNote = 'Only one report runs at a time. Yours can start when it finishes.';
  } else if (!loading && stations.length === 0) {
    runNote = '';
  } else if (status?.aiReady === false) {
    runNote = blockText || '';
  } else if (dailyLimitHit) {
    runLabel = 'Daily limit reached';
    runNote = `All ${runsAllowed} reports for today are used. More can run tomorrow.`;
  } else if (coolingDown) {
    runLabel = `Next run in ${Math.max(1, Math.ceil(cooldownLeftMs / 60000))} min`;
    runNote = `Reports run ${cooldownMinutes} minutes apart. The next can start at ${new Date(nextRunAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.`;
  } else if (stations.length > 0 && selectedIds.length === 0) {
    runLabel = 'Tick a station';
    runNote = 'Tick at least one station below.';
  } else if (!includeAdds && !includeMoves) {
    runLabel = 'Choose what to include';
    runNote = 'Tick tracks to add, moves and drops, or both.';
  }
  const runFor = loading
    ? ''
    : stations.length === 0
      ? 'No stations yet'
      : selectedStations.length === 0
        ? 'No stations ticked'
        : selectedStations.length === 1
          ? selectedStations[0].name
          : `${selectedStations.length} ticked stations`;
  const showSidebar = view === 'reports' || stations.length > 1;
  const workingQuestion = asking ? (askedRef.current || question) : question;
  const autoSearch = trackAsk(question) || searchAsk(question);
  const lookupOn = searchQuestion || trackAsk(workingQuestion) || searchAsk(workingQuestion);
  const liveSteps = asking ? askStepsFor(workingQuestion, lookupOn) : REPORT_STEPS;
  const liveLabel = asking
    ? liveSteps[Math.min(agentStep, liveSteps.length - 1)]
    : ownReportRunning
      ? REPORT_STEPS[Math.min(agentStep, REPORT_STEPS.length - 1)]
      : questionInProgress
        ? 'Answering'
        : 'Ready';
  const busyHere = asking || ownReportRunning || questionInProgress;
  const thread = [...questions].reverse();
  const showingReport = view === 'reports' && detailOpen;
  const artworkTracks = useMemo(() => {
    const picked = [];
    const seen = new Set();
    let candidates = [];
    if (view === 'ask') candidates = questions.slice(0, 4).flatMap((item) => readAnswer(item.answer).tracks || []);
    else if (detailOpen) candidates = activeReport?.recommendations || [];
    for (const track of candidates) {
      const key = trackArtKey(track);
      if (!track?.title || !track?.artist || !key || key === '|' || seen.has(key)) continue;
      seen.add(key);
      picked.push(track);
      if (picked.length >= 100) break;
    }
    return picked;
  }, [activeReport, questions, detailOpen, view]);
  const artwork = useArtworkMap(artworkTracks);
  const artworkRef = useRef(artwork);
  artworkRef.current = artwork;

  useEffect(() => {
    if (!asking && !reportRunning) return undefined;
    setAgentStep(0);
    const timer = setInterval(() => setAgentStep((step) => step + 1), 2600);
    return () => clearInterval(timer);
  }, [asking, reportRunning]);

  useEffect(() => {
    if (view !== 'ask') return undefined;
    const node = threadRef.current;
    if (node) node.scrollTop = node.scrollHeight;
    return undefined;
  }, [questions.length, asking, view]);

  useEffect(() => {
    if (!showingReport) return undefined;
    const node = threadRef.current;
    if (node) node.scrollTop = 0;
    return undefined;
  }, [showingReport, activeReport?.id]);

  return (
    <Box
      sx={{
        m: -3,
        height: 'var(--rs-vh)',
        maxHeight: 'var(--rs-vh)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          px: { xs: 2, md: 3 },
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          flexShrink: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: (theme) => theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(12,16,24,0.2) 100%)'
            : 'linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0) 100%)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: (theme) => theme.palette.mode === 'dark'
              ? 'linear-gradient(90deg, rgba(34,211,238,0.85), rgba(59,130,246,0.25) 42%, transparent 74%)'
              : 'linear-gradient(90deg, rgba(8,145,178,0.7), rgba(59,130,246,0.18) 42%, transparent 74%)',
          },
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '14px',
            overflow: 'hidden',
            flexShrink: 0,
            bgcolor: '#050505',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 12px 28px -16px rgba(0,0,0,0.8)',
          }}
        >
          <Box
            component="img"
            src={radioSuiteLogo}
            alt="The Radio Suite"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scale(1.9)' }}
          />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.15 }}>Intelligence</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: busyHere ? 'info.main' : 'success.main',
                boxShadow: busyHere ? '0 0 0 4px rgba(34,211,238,0.18)' : '0 0 0 4px rgba(34,197,94,0.15)',
              }}
            />
            <Typography variant="body2" color="text.secondary" noWrap>
              {liveLabel}
            </Typography>
          </Box>
        </Box>
        {view === 'ask' ? (
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, letterSpacing: '0.06em' }}>
              QUESTIONS TODAY
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {questionStats?.askedToday || 0} of {questionStats?.maxPerDay || 20}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, letterSpacing: '0.06em' }}>
              REPORTS TODAY
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {runsToday} of {runsAllowed}
            </Typography>
            <QuotaPips used={runsToday} total={runsAllowed} />
          </Box>
        )}
      </Box>

      <Box
        role="tablist"
        aria-label="Intelligence"
        onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const next = view === 'reports' ? 'ask' : 'reports';
          setView(next);
          document.getElementById(`intelligence-tab-${next}`)?.focus();
        }}
        sx={{
          display: 'flex',
          gap: 1.25,
          px: { xs: 2, md: 3 },
          py: 1.25,
          flexShrink: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <ModeCard
          value="reports"
          selected={view === 'reports'}
          onSelect={setView}
          icon={<AssessmentOutlined />}
          title="Station reports"
          detail={ownReportRunning ? 'Report running' : 'Tracks to add, moves, and drops'}
          busy={ownReportRunning}
        />
        <ModeCard
          value="ask"
          selected={view === 'ask'}
          onSelect={setView}
          icon={<ForumOutlined />}
          title="Ask Intelligence"
          detail={asking || questionInProgress ? 'Answering' : 'Chat about rotations, moves, and adds'}
          busy={asking || questionInProgress}
        />
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, pt: (message || (status && !status.aiReady) || missingCountry.length) ? 1.5 : 0, flexShrink: 0 }}>
        {message && (
          <Alert severity={message.type === 'error' ? 'error' : message.type === 'info' ? 'info' : 'success'} sx={{ mb: 1.5 }} onClose={() => setMessage(null)}>
            {visibleText(message.text)}
          </Alert>
        )}
        {status && !status.aiReady && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {visibleText(status.blockers?.join(' '))}
            {isSuperAdmin() && (
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" onClick={() => navigate('/settings?tab=station')}>System features</Button>
                <Button size="small" onClick={() => navigate('/settings?tab=ai_features')}>API key</Button>
              </Box>
            )}
          </Alert>
        )}
        {missingCountry.length > 0 && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Set a country for {missingCountry.map((station) => station.name).join(', ')} so suggestions match the market.
            {isSuperAdmin() && (
              <Button size="small" sx={{ ml: 1 }} onClick={() => navigate('/settings?tab=station')}>
                Edit stations
              </Button>
            )}
          </Alert>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: showSidebar ? { xs: '1fr', md: '340px minmax(0, 1fr)' } : '1fr',
          gridTemplateRows: showSidebar ? { xs: 'minmax(0, calc(var(--rs-vh) * 0.32)) minmax(0, 1fr)', md: 'minmax(0, 1fr)' } : 'minmax(0, 1fr)',
        }}
      >
        {showSidebar && (
          <Box
            sx={{
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRight: { md: '1px solid' },
              borderBottom: { xs: '1px solid', md: 'none' },
              borderColor: 'divider',
              background: (theme) => theme.palette.mode === 'dark'
                ? 'linear-gradient(180deg, rgba(255,255,255,0.025), transparent 120px)'
                : 'linear-gradient(180deg, rgba(255,255,255,0.45), transparent 120px)',
            }}
          >
            {view === 'reports' && (
              <Box
                sx={{
                  flexShrink: 0,
                  px: 2,
                  pt: 2,
                  pb: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: (theme) => alpha(theme.palette.info.main, runBlocked ? 0.02 : 0.05),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    New report
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {runFor || '\u00a0'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 0.5 }}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={<Checkbox size="small" checked={includeAdds} disabled={starting} onChange={(event) => setIncludeAdds(event.target.checked)} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Tracks to add</Typography>}
                  />
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={<Checkbox size="small" checked={includeMoves} disabled={starting} onChange={(event) => setIncludeMoves(event.target.checked)} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Moves and drops</Typography>}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25, opacity: includeAdds ? 1 : 0.5 }}>
                  <Box component="label" htmlFor="intelligence-track-count" sx={{ minWidth: 0 }}>
                    <Typography component="span" variant="body2" sx={{ display: 'block', fontWeight: 600 }}>Tracks per station</Typography>
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>1–{maxTracks}</Typography>
                  </Box>
                  <TextField
                    id="intelligence-track-count"
                    size="small"
                    value={trackCountInput}
                    placeholder={String(defaultTracks)}
                    onChange={(event) => setTrackCountInput(event.target.value.replace(/\D/g, '').slice(0, 3))}
                    onBlur={() => setTrackCountInput((current) => (current === '' ? '' : String(clampTrackCount(current, maxTracks, defaultTracks))))}
                    disabled={starting || !includeAdds}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { textAlign: 'center', fontWeight: 700 } }}
                    sx={{ width: 76, flexShrink: 0 }}
                  />
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={runBlocked}
                  onClick={runReport}
                  startIcon={starting || ownReportRunning
                    ? <CircularProgress size={16} color="inherit" />
                    : coolingDown || reportInProgress ? <Schedule /> : <AutoAwesome />}
                  sx={{
                    fontWeight: 700,
                    boxShadow: (theme) => (theme.palette.mode === 'dark' ? '0 10px 24px -12px rgba(59,130,246,0.9)' : 'none'),
                    '&.Mui-disabled': {
                      color: 'text.secondary',
                      bgcolor: 'action.selected',
                      backgroundImage: 'none',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {runLabel}
                </Button>
                {runNote && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.45 }}>
                    {runNote}
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2, py: 2 }}>
            {stations.length > 1 && (
              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="overline" color="text.secondary">Stations</Typography>
                  <Box>
                    <Button size="small" onClick={() => setSelectedIds(stations.map((station) => station.id))}>All</Button>
                    <Button size="small" onClick={() => setSelectedIds([])} disabled={selectedIds.length === 0}>Clear</Button>
                  </Box>
                </Box>
                {loading ? (
                  <CircularProgress size={22} />
                ) : (
                  stations.map((station) => {
                    const selected = selectedIds.includes(station.id);
                    return (
                      <FormControlLabel
                        key={station.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          m: 0,
                          mb: 0.75,
                          pl: 0.5,
                          pr: 1.25,
                          py: 0.25,
                          width: '100%',
                          borderRadius: '12px',
                          border: '1px solid',
                          borderColor: selected ? 'info.main' : 'divider',
                          bgcolor: (theme) => (selected ? alpha(theme.palette.info.main, 0.08) : 'transparent'),
                          transition: 'border-color 150ms ease, background-color 150ms ease',
                          '&:hover': {
                            borderColor: selected ? 'info.main' : 'text.disabled',
                            bgcolor: (theme) => (selected ? alpha(theme.palette.info.main, 0.12) : theme.palette.action.hover),
                          },
                          '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
                        }}
                        control={<Checkbox size="small" checked={selected} onChange={() => toggleStation(station.id)} />}
                        label={(
                          <Box sx={{ py: 0.4, minWidth: 0 }}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: selected ? 700 : 500, lineHeight: 1.35 }}>{station.name}</Typography>
                            <Typography variant="caption" color={station.country ? 'text.secondary' : 'warning.main'} sx={{ display: 'block', lineHeight: 1.35 }}>
                              {stationMarket(station) || 'Country not set'}
                            </Typography>
                          </Box>
                        )}
                      />
                    );
                  })
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {view === 'ask'
                    ? (!focusQuestion
                      ? 'Questions cover every station. Turn on Focus on ticked stations below to narrow them.'
                      : selectedIds.length
                        ? `Questions cover the ${selectedIds.length} ticked ${selectedIds.length === 1 ? 'station' : 'stations'}.`
                        : 'Tick a station, or turn off Focus on ticked stations, to ask.')
                    : `${selectionLabel} for the next report${includeAdds ? ` · ${trackCount} ${trackCount === 1 ? 'track' : 'tracks'} each` : ''}`}
                </Typography>
              </Box>
            )}

            {view === 'reports' && selectedStations.length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>Categories to look at</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.45 }}>
                  Adds go only into the ticked categories, and moves and drops are suggested from them.
                </Typography>
                {selectedStations.map((station) => (
                  <CategoryPicker
                    key={station.id}
                    station={station}
                    info={categoryInfo[station.id]}
                    chosen={categoryChoice[station.id]}
                    disabled={starting}
                    onChange={(ids) => setCategoryChoice((current) => ({ ...current, [station.id]: ids }))}
                  />
                ))}
              </Box>
            )}

            {view === 'reports' && (
              <>
                <Typography variant="overline" color="text.secondary">Reports</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                  Kept for {reportRetentionDays} days.
                </Typography>
                {reports.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {stations.length > 1 ? 'Tick a station, then run a report.' : 'Run a report to see tracks to add.'}
                  </Typography>
                ) : (
                  reports.map((report) => {
                    const names = (report.profiles || []).map((profile) => (
                      profile.format ? `${profile.name} · ${profile.format}` : profile.name
                    )).filter(Boolean).join(', ');
                    const meta = statusMeta(report.status);
                    const parts = reportIncludes(report);
                    const count = [
                      parts.adds && typeof report.recommendationCount === 'number' ? `${report.recommendationCount} adds` : '',
                      parts.review && typeof report.moveCount === 'number' ? `${report.moveCount} moves` : '',
                    ].filter(Boolean).join(', ');
                    const open = detailOpen && activeReport?.id === report.id;
                    return (
                      <Box
                        key={report.id}
                        onClick={() => openReport(report)}
                        sx={{
                          mt: 1,
                          px: 1.25,
                          py: 1.15,
                          borderRadius: '12px',
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: open ? 'info.main' : 'divider',
                          bgcolor: open ? 'action.selected' : 'transparent',
                          boxShadow: open ? '0 0 0 1px rgba(34,211,238,0.18)' : 'none',
                          transition: 'border-color 150ms ease, background-color 150ms ease',
                          '&:hover': { bgcolor: 'action.hover', borderColor: open ? 'info.main' : 'text.disabled' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{report.weekday || 'Report'}</Typography>
                          <Chip size="small" label={meta.label} color={meta.color} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }} noWrap>
                          {names || `${(report.stationIds || []).length} stations`}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatWhen(report.createdAt)}
                            {count ? ` · ${count}` : ''}
                          </Typography>
                          <IconButton
                            size="small"
                            aria-label="Download PDF"
                            disabled={pdfBusy || report.status !== 'completed'}
                            onClick={(event) => {
                              event.stopPropagation();
                              downloadReport(report);
                            }}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    );
                  })
                )}
                {isSuperAdmin() && (
                  <Button size="small" sx={{ mt: 2 }} onClick={() => navigate('/settings?tab=users')}>
                    Enable for a person
                  </Button>
                )}
              </>
            )}
            </Box>
          </Box>
        )}

        <Box sx={{ minWidth: 0, minHeight: 0, alignSelf: 'stretch', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box
            ref={threadRef}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              px: { xs: 2, md: 3.5 },
              py: 2.5,
            }}
          >
            {view === 'reports' && !detailOpen ? (
              <Box sx={{ m: 'auto', maxWidth: 560, textAlign: 'center', py: 7, px: 3 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                    borderRadius: '18px',
                    display: 'grid',
                    placeItems: 'center',
                    color: 'info.main',
                    border: '1px solid',
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.35),
                    bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
                  }}
                >
                  <AssessmentOutlined sx={{ fontSize: 32 }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Station reports
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: 2.5 }}>
                  {stations.length > 1 ? 'Tick the stations on the left, choose' : 'Choose'} tracks to add, moves and drops, or both, then tick the categories to look at, and run the report.
                </Typography>
                <Box sx={{ display: 'grid', gap: 1.25, textAlign: 'left' }}>
                  {[
                    ['Reads the format', 'Format, roles, clocks and spins decide what belongs where, from the library rather than a guess.'],
                    ['Never repeats a song', 'Every pick is checked against the whole library, so a song already in the system, in any mix or credit, is dropped.'],
                    ['Dates every pick', 'The real release date is looked up in a music catalogue before a song is kept, so an older record cannot pass as a new release.'],
                  ].map(([title, detail]) => (
                    <Box
                      key={title}
                      sx={{
                        display: 'flex',
                        gap: 1.25,
                        alignItems: 'flex-start',
                        p: 1.5,
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: (theme) => alpha(theme.palette.info.main, 0.04),
                      }}
                    >
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'info.main', mt: 0.85, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{title}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>{detail}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, lineHeight: 1.5 }}>
                  Reports are kept for {reportRetentionDays} days.{reports.length ? ' Open a past one from the Reports list.' : ''}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<ForumOutlined />}
                  onClick={() => setView('ask')}
                  sx={{ mt: 2.5 }}
                >
                  Ask a question instead
                </Button>
              </Box>
            ) : showingReport ? (
              detailLoading || !activeReport ? (
                <CircularProgress size={28} />
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                        {activeReport.weekday ? `${activeReport.weekday} report` : 'Report'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {[
                          formatWhen(activeReport.createdAt),
                          reportIncludes(activeReport).adds
                            ? `${(activeReport.recommendations || []).length} ${(activeReport.recommendations || []).length === 1 ? 'track' : 'tracks'} to add`
                            : '',
                          reportIncludes(activeReport).review
                            ? `${(activeReport.moves || []).length} ${(activeReport.moves || []).length === 1 ? 'move or drop' : 'moves and drops'}`
                            : '',
                        ].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      <Chip size="small" label={statusMeta(activeReport.status).label} color={statusMeta(activeReport.status).color} />
                      <Button
                        variant="contained"
                        startIcon={pdfBusy ? <CircularProgress size={16} color="inherit" /> : <Download />}
                        disabled={pdfBusy || activeReport.status !== 'completed'}
                        onClick={() => downloadReport(activeReport)}
                      >
                        {pdfBusy ? 'Preparing' : 'PDF'}
                      </Button>
                      <IconButton aria-label="Close report" onClick={() => setDetailOpen(false)}>
                        <Close />
                      </IconButton>
                    </Box>
                  </Box>
                  {activeReport.status === 'running' && !asking && (
                    <AgentTrace steps={REPORT_STEPS} step={agentStep} />
                  )}
                  {visibleText(activeReport.error) && (
                    <Alert severity="error" sx={{ mb: 2 }}>{visibleText(activeReport.error)}</Alert>
                  )}
                  {activeReport.status === 'running' && (activeReport.profiles || []).length === 0 && (
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      Working through the stations. Tracks appear as they are found.
                    </Typography>
                  )}
                  {(activeReport.profiles || []).map((profile, profileIndex, profiles) => {
                    const parts = reportIncludes(activeReport);
                    const tracks = tracksForStation(activeReport, profile);
                    const moves = movesForStation(activeReport, profile);
                    const summary = visibleText(profile.summary);
                    const focus = visibleText(profile.focus);
                    const reviewSummary = visibleText(profile.reviewSummary);
                    const working = activeReport.status === 'running' && !profile.error && profileIndex === profiles.length - 1;
                    const shortfall = working ? '' : shortfallNote(profile, tracks.length);
                    return (
                      <Box key={profile.stationId} sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>{profile.name}</Typography>
                          {profile.format && <Chip size="small" label={profile.format} />}
                          {profile.confidence && (
                            <Typography variant="caption" color="text.secondary">
                              {capitalise(profile.confidence)} confidence
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {[profile.location, profile.country].filter(Boolean).join(', ') || 'Country not set'}
                          {profile.librarySize ? ` · ${profile.librarySize} in the library` : ''}
                        </Typography>
                        {(profile.formatEvidence?.length > 0 || profile.categories?.length > 0) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, maxWidth: 920, lineHeight: 1.5 }}>
                            {[
                              profile.formatEvidence?.length ? `Format read from ${profile.formatEvidence.slice(0, 3).join(', ')}` : '',
                              profile.categories?.length ? `Categories looked at: ${profile.categories.join(', ')}` : '',
                            ].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                        {verifiedNote(profile) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, maxWidth: 920, lineHeight: 1.5 }}>
                            {verifiedNote(profile)}.
                          </Typography>
                        )}
                        {profile.error && (
                          <Alert severity="error" sx={{ mt: 1.5 }}>{visibleText(profile.error)}</Alert>
                        )}
                        {summary && (
                          <Typography variant="body1" sx={{ mt: 1.5, maxWidth: 920, lineHeight: 1.65, overflowWrap: 'anywhere' }}>
                            {summary}
                          </Typography>
                        )}
                        {focus && focus !== summary && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 920, lineHeight: 1.65, overflowWrap: 'anywhere' }}>
                            {focus}
                          </Typography>
                        )}
                        {!parts.adds ? null : tracks.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                            {working ? 'Still looking for tracks.' : (shortfall || 'No new tracks to add.')}
                          </Typography>
                        ) : (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="overline" color="text.secondary">
                              {working && activeReport.trackCount > tracks.length
                                ? `${tracks.length} of ${activeReport.trackCount} tracks so far`
                                : `${tracks.length} ${tracks.length === 1 ? 'track' : 'tracks'} to add`}
                            </Typography>
                            {!working && shortfall && (
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 920 }}>
                                {shortfall}
                              </Typography>
                            )}
                            <Box sx={{ ...trackGridSx, mt: 1.25 }}>
                              {tracks.map((track, index) => (
                                <TrackSuggestion key={`${track.artist}-${track.title}-${index}`} track={track} index={index} artUrl={artwork[trackArtKey(track)]} showTempo={profile.beatmix === true} />
                              ))}
                            </Box>
                          </Box>
                        )}
                        {parts.review && !profile.error && (
                          <Box sx={{ mt: 2.5 }}>
                            <Typography variant="overline" color="text.secondary">
                              {moves.length} {moves.length === 1 ? 'move or drop' : 'moves and drops'}
                            </Typography>
                            {profile.reviewError && (
                              <Alert severity="warning" sx={{ mt: 0.5 }}>{visibleText(profile.reviewError)}</Alert>
                            )}
                            {reviewSummary && (
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 920, lineHeight: 1.6 }}>
                                {reviewSummary}
                              </Typography>
                            )}
                            {moves.length > 0 ? (
                              <Box sx={{ ...trackGridSx, mt: 1.25 }}>
                                {moves.map((move, index) => (
                                  <MoveSuggestion key={`${move.from}-${move.artist}-${move.title}-${index}`} move={move} index={index} />
                                ))}
                              </Box>
                            ) : !profile.reviewError && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {typeof profile.moveCount === 'number' ? 'No moves suggested.' : activeReport.status === 'running' ? 'Still reviewing the categories.' : 'The review did not finish.'}
                              </Typography>
                            )}
                          </Box>
                        )}
                        <SourceLinks sources={profile.sources} />
                      </Box>
                    );
                  })}
                </Box>
              )
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
                {thread.length === 0 && !asking && (
                  <Box
                    sx={{
                      m: 'auto',
                      maxWidth: 560,
                      textAlign: 'center',
                      py: 7,
                      px: 3,
                    }}
                  >
                    <Box
                      sx={{
                        width: 88,
                        height: 88,
                        mx: 'auto',
                        mb: 2,
                        borderRadius: '18px',
                        overflow: 'hidden',
                        bgcolor: '#050505',
                        border: '1px solid rgba(255,255,255,0.08)',
                        boxShadow: '0 18px 40px -20px rgba(0,0,0,0.75)',
                      }}
                    >
                      <Box
                        component="img"
                        src={radioSuiteLogo}
                        alt=""
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scale(1.9)' }}
                      />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                      Ask Intelligence
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                      Ask about rotations, artists, tracks to add, or what to move between categories and drop from Main. Name a station and category for moves, or tick Search online to check the web.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.6 }}>
                      Tracks already in the system are never suggested, and every track&apos;s release date is checked so an old record is never offered as a new release. Answers are suggestions only. Nothing is moved into a category, and nothing is written to the library.
                    </Typography>
                  </Box>
                )}
                {thread.map((item) => {
                  const parsed = readAnswer(item.answer);
                  const failed = item.status === 'failed';
                  const scope = (item.stationIds || []).length
                    ? `${item.stationIds.length} ${item.stationIds.length === 1 ? 'station' : 'stations'}`
                    : 'All stations';
                  return (
                    <Box key={item.id} sx={{ mb: 3.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Box sx={{ maxWidth: 760 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mb: 0.5 }}>
                            You · {formatWhen(item.createdAt)} · {scope}
                          </Typography>
                          <Box
                            sx={{
                              px: 2,
                              py: 1.5,
                              borderRadius: '16px 16px 6px 16px',
                              border: '1px solid',
                              borderColor: 'divider',
                              background: (theme) => theme.palette.mode === 'dark'
                                ? 'linear-gradient(180deg, rgba(59,130,246,0.16), rgba(22,27,37,0.55))'
                                : 'linear-gradient(180deg, rgba(59,130,246,0.1), #ffffff)',
                            }}
                          >
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                              {item.question}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          mt: 1.5,
                          maxWidth: 1100,
                          pl: 1.75,
                          borderLeft: '2px solid',
                          borderColor: failed ? 'error.main' : 'info.main',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, letterSpacing: '0.04em' }}>
                          INTELLIGENCE{failed ? ' · FAILED' : ''}{item.usedWebSearch ? ' · SEARCHED ONLINE' : ''}
                        </Typography>
                        {failed ? (
                          <Alert severity="error">{visibleText(item.error || item.answer) || 'That question failed.'}</Alert>
                        ) : (
                          <>
                            {parsed.lead && (
                              <Typography variant="body1" sx={{ lineHeight: 1.65, maxWidth: 860 }}>
                                {parsed.lead}
                              </Typography>
                            )}
                            {parsed.tracks.length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="overline" color="text.secondary">
                                  {parsed.tracks.length} {parsed.tracks.length === 1 ? 'track' : 'tracks'} to add
                                </Typography>
                                <Box sx={{ ...trackGridSx, mt: 1.25 }}>
                                  {parsed.tracks.map((track, index) => (
                                    <TrackSuggestion key={`${track.artist}-${track.title}-${index}`} track={track} index={index} artUrl={artwork[trackArtKey(track)]} showTempo={track.bpm != null} />
                                  ))}
                                </Box>
                              </Box>
                            )}
                            {parsed.moves.length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="overline" color="text.secondary">
                                  {parsed.moves.length} {parsed.moves.length === 1 ? 'move or drop' : 'moves and drops'}
                                </Typography>
                                <Box sx={{ ...trackGridSx, mt: 1.25 }}>
                                  {parsed.moves.map((move, index) => (
                                    <MoveSuggestion key={`${move.from}-${move.artist}-${move.title}-${index}`} move={move} index={index} />
                                  ))}
                                </Box>
                              </Box>
                            )}
                            <SourceLinks sources={parsed.sources} />
                            {!parsed.lead && parsed.tracks.length === 0 && parsed.moves.length === 0 && (
                              <Typography variant="body2" color="text.secondary">
                                {item.status === 'running' ? 'Answering…' : ''}
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                {asking && (
                  <AgentTrace steps={askStepsFor(workingQuestion, lookupOn)} step={agentStep} />
                )}
              </Box>
            )}
          </Box>

          {view === 'ask' && (
            <Box
              sx={{
                flexShrink: 0,
                mx: { xs: 1.5, md: 2.5 },
                mb: 1.75,
                px: { xs: 1.5, md: 2 },
                py: 1.5,
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'linear-gradient(180deg, rgba(255,255,255,0.045), rgba(22,27,37,0.72))'
                  : 'linear-gradient(180deg, #ffffff, #f8fafc)',
                boxShadow: (theme) => theme.palette.mode === 'dark'
                  ? '0 18px 40px -28px rgba(0,0,0,0.9)'
                  : '0 16px 36px -28px rgba(15,23,42,0.25)',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
                {ASK_PROMPTS.map((prompt) => (
                  <Chip
                    key={prompt}
                    label={prompt}
                    size="small"
                    variant={question === prompt ? 'filled' : 'outlined'}
                    color={question === prompt ? 'primary' : 'default'}
                    onClick={() => setQuestion(prompt)}
                    disabled={asking}
                    sx={{ height: 'auto', py: 0.6, '& .MuiChip-label': { whiteSpace: 'normal' } }}
                  />
                ))}
              </Box>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                value={question}
                onChange={(event) => setQuestion(event.target.value.slice(0, 1200))}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    const blocked = asking || !question.trim() || !status?.aiReady || status?.questions?.canAsk === false || (focusQuestion && selectedIds.length === 0);
                    if (!blocked) askQuestion();
                  }
                }}
                placeholder={askBeatmix
                  ? 'Add 20 tracks at 120–128 BPM, extended mixes, artists with no spins this week'
                  : 'Add 20 tracks that fit the clock length, artists with no spins this week'}
                disabled={asking}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                  {stations.length > 1 && (
                    <FormControlLabel
                      control={(
                        <Checkbox
                          size="small"
                          checked={focusQuestion}
                          onChange={(event) => setFocusQuestion(event.target.checked)}
                        />
                      )}
                      label="Focus on ticked stations"
                    />
                  )}
                  <FormControlLabel
                    control={(
                      <Checkbox
                        size="small"
                        checked={lookupOn}
                        disabled={autoSearch}
                        onChange={(event) => setSearchQuestion(event.target.checked)}
                      />
                    )}
                    label={trackAsk(question) ? 'Checking current releases' : searchAsk(question) ? 'Searching online' : 'Search online'}
                  />
                </Box>
                <Button
                  variant="contained"
                  disabled={asking || !question.trim() || !status?.aiReady || status?.questions?.canAsk === false || (focusQuestion && selectedIds.length === 0)}
                  onClick={askQuestion}
                  startIcon={asking ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                >
                  Ask
                </Button>
              </Box>
              {status?.questions?.blockReason && status?.aiReady && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  {status.questions.blockReason}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
